import { useEffect, useState } from "react";
import { Save, Mail } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminListEmailTemplates, adminUpdateEmailTemplate } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function AdminEmailTemplates() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject: "", body_html: "", description: "" });

  function load() {
    adminListEmailTemplates().then((d) => {
      setTemplates(d.templates || []);
      if (!active && d.templates?.length) {
        setActive(d.templates[0].key);
        setForm({
          subject: d.templates[0].subject,
          body_html: d.templates[0].body_html,
          description: d.templates[0].description || "",
        });
      }
    });
  }
  useEffect(load, []); // eslint-disable-line

  function select(t) {
    setActive(t.key);
    setForm({ subject: t.subject, body_html: t.body_html, description: t.description || "" });
  }

  async function save() {
    try {
      await adminUpdateEmailTemplate(active, { key: active, ...form });
      toast({ title: "Template opgeslagen" });
      load();
    } catch (err) {
      toast({ title: "Fout", description: err?.response?.data?.detail || "Probeer opnieuw", variant: "destructive" });
    }
  }

  return (
    <AdminLayout>
      <div data-testid="admin-templates-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">E-mail templates</h1>
            <p className="text-ink-soft mt-1 text-sm">Pas onderwerp en HTML aan. Placeholders: {`{{name}}, {{checker_name}}, {{score}}, {{report_url}}, {{code}}, …`}</p>
          </div>
          {active && (
            <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold" data-testid="save-template">
              <Save className="h-4 w-4" /> Opslaan
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-3 space-y-1">
            {templates.map((t) => (
              <button
                key={t.key}
                onClick={() => select(t)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                  active === t.key ? "bg-ink text-white border-ink" : "bg-white border-border text-ink hover:border-ink-soft"
                }`}
                data-testid={`tpl-${t.key}`}
              >
                <div className="font-semibold text-sm flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" /> {t.key}
                </div>
                <div className={`text-xs ${active === t.key ? "text-blue-200" : "text-ink-soft"} mt-0.5 truncate`}>{t.description || t.subject}</div>
              </button>
            ))}
          </aside>

          {active && (
            <section className="lg:col-span-9 rounded-2xl bg-white border border-border p-6 space-y-4">
              <div>
                <Label>Beschrijving (admin notitie)</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>Onderwerp</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="tpl-subject" />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <Textarea
                  rows={20}
                  className="font-mono text-xs"
                  value={form.body_html}
                  onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                  data-testid="tpl-body"
                />
              </div>
              <div className="text-xs text-ink-soft">
                <p><b>Beschikbare placeholders</b>: <code>{`{{checker_name}}`}</code>, <code>{`{{score}}`}</code>, <code>{`{{report_url}}`}</code>, <code>{`{{first_name}}`}</code>, <code>{`{{name}}`}</code>, <code>{`{{business_name}}`}</code>, <code>{`{{code}}`}</code>, <code>{`{{credits_total}}`}</code>, <code>{`{{credits_url}}`}</code>, <code>{`{{reset_link}}`}</code></p>
              </div>
            </section>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
